import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileAudio,
  FileIcon,
  FileText,
  FileVideo,
  ImageIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { downloadMedia } from "@/components/ui/MediaBlocks/utils";
import { cn } from "@/lib/utils";
import {
  isBrowserRenderableMediaUrl,
  useOutputMediaContentUrl,
} from "@/queries/vector-store/useVectorStoreFileContent";
import {
  getMediaKind,
  getMediaLabel,
  type OutputMediaItem,
} from "@/routes/smart-table/utils/multiContent";

interface MediaPreviewCarouselProps {
  items: OutputMediaItem[];
}

function MediaIcon({
  mediaType,
  className = "h-4 w-4",
}: {
  mediaType?: string;
  className?: string;
}) {
  const kind = getMediaKind(mediaType);

  if (kind === "image") {
    return <ImageIcon className={className} />;
  }
  if (kind === "video") {
    return <FileVideo className={className} />;
  }
  if (kind === "audio") {
    return <FileAudio className={className} />;
  }
  if (kind === "pdf") {
    return <FileText className={className} />;
  }
  return <FileIcon className={className} />;
}

function MediaPreviewSurface({
  item,
  url,
  isLoading,
}: {
  item: OutputMediaItem;
  url?: string;
  isLoading: boolean;
}) {
  const label = getMediaLabel(item);
  const kind = getMediaKind(item.media_type);

  if (!url) {
    return (
      <div className="flex min-h-[360px] w-full flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border/60 bg-muted/20 p-8 text-center">
        <MediaIcon mediaType={item.media_type} className="h-9 w-9 text-muted-foreground/50" />
        <div>
          <p className="text-sm font-medium text-foreground/80">
            {isLoading ? "Loading preview…" : "Preview unavailable"}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {isLoading
              ? "Resolving the uploaded file."
              : "Use JSON or Text mode to inspect the stored value."}
          </p>
        </div>
      </div>
    );
  }

  if (kind === "image") {
    return (
      <div className="flex min-h-[360px] w-full items-center justify-center rounded-lg border border-border/40 bg-muted/20 p-3">
        <img
          src={url}
          alt={label}
          className="max-h-[62vh] max-w-full rounded-md object-contain shadow-sm"
          loading="lazy"
        />
      </div>
    );
  }

  if (kind === "video") {
    return (
      <div className="flex min-h-[360px] w-full items-center justify-center rounded-lg border border-border/40 bg-muted/20 p-3">
        <video
          src={url}
          controls
          className="max-h-[62vh] max-w-full rounded-md shadow-sm outline-none"
        >
          <track kind="captions" />
        </video>
      </div>
    );
  }

  if (kind === "audio") {
    return (
      <div className="flex min-h-[260px] w-full items-center justify-center rounded-lg border border-border/40 bg-muted/20 p-6">
        <div className="w-full max-w-xl rounded-xl border border-border/50 bg-background p-5 shadow-sm">
          <div className="mb-4 flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
            <FileAudio className="h-5 w-5 shrink-0" />
            <span className="truncate">{label}</span>
          </div>
          <audio src={url} controls className="w-full outline-none">
            <track kind="captions" />
          </audio>
        </div>
      </div>
    );
  }

  if (kind === "pdf") {
    return (
      <div className="h-[62vh] w-full overflow-hidden rounded-lg border border-border/40 bg-white shadow-sm">
        <iframe src={url} className="h-full w-full" title={label} />
      </div>
    );
  }

  return (
    <div className="flex min-h-[260px] w-full items-center justify-center rounded-lg border border-border/40 bg-muted/20 p-8">
      <div className="flex max-w-md flex-col items-center gap-4 rounded-xl border border-border/50 bg-background p-8 text-center shadow-sm">
        <FileIcon className="h-10 w-10 text-muted-foreground/50" />
        <div>
          <p className="text-sm font-medium text-foreground">{label}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            This file type cannot be previewed inline.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
          >
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
            Open
          </Button>
          <Button type="button" size="sm" onClick={() => downloadMedia(url, label)}>
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Download
          </Button>
        </div>
      </div>
    </div>
  );
}

export function MediaPreviewCarousel({ items }: MediaPreviewCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const safeItems = useMemo(() => items.filter(Boolean), [items]);
  const hasMultiple = safeItems.length > 1;
  const activeItem = safeItems[activeIndex] ?? safeItems[0];

  useEffect(() => {
    setActiveIndex(index => Math.min(index, Math.max(safeItems.length - 1, 0)));
  }, [safeItems.length]);

  const directUrl = isBrowserRenderableMediaUrl(activeItem?.url) ? activeItem?.url : undefined;
  const { previewUrl, isLoading } = useOutputMediaContentUrl(
    directUrl ? null : (activeItem?.storage_key ?? null),
  );
  const url = directUrl ?? previewUrl;

  if (safeItems.length === 0 || !activeItem) {
    return null;
  }

  const resolvedActiveIndex = Math.min(activeIndex, safeItems.length - 1);
  const label = getMediaLabel(activeItem, resolvedActiveIndex);
  const goPrevious = () => setActiveIndex(index => (index <= 0 ? safeItems.length - 1 : index - 1));
  const goNext = () => setActiveIndex(index => (index >= safeItems.length - 1 ? 0 : index + 1));

  return (
    <div className="flex w-full max-w-5xl flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <MediaIcon
            mediaType={activeItem.media_type}
            className="h-4 w-4 shrink-0 text-muted-foreground"
          />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground">{label}</p>
            {hasMultiple ? (
              <p className="text-xs text-muted-foreground">
                {resolvedActiveIndex + 1} of {safeItems.length}
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {hasMultiple ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="tinyIcon"
                onClick={goPrevious}
                aria-label="Previous file"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="tinyIcon"
                onClick={goNext}
                aria-label="Next file"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          ) : null}
          {url ? (
            <Button
              type="button"
              variant="ghost"
              size="tinyIcon"
              onClick={() => downloadMedia(url, label)}
              aria-label="Download file"
            >
              <Download className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>

      <MediaPreviewSurface item={activeItem} url={url} isLoading={isLoading} />

      {hasMultiple ? (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {safeItems.map((item, index) => (
            <button
              key={item.storage_key ?? item.url ?? item.title ?? getMediaLabel(item, index)}
              type="button"
              className={cn(
                "flex max-w-44 shrink-0 items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs transition-colors",
                index === resolvedActiveIndex
                  ? "border-primary/50 bg-primary/10 text-foreground"
                  : "border-border/50 bg-muted/30 text-muted-foreground hover:bg-muted/60",
              )}
              onClick={() => setActiveIndex(index)}
            >
              <MediaIcon mediaType={item.media_type} className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{getMediaLabel(item, index)}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
